package app.plugin

import app.model.DeployableData
import app.model.ProjectVariableValue
import app.model.Step
import io.ktor.resources.*
import kotlinx.serialization.Serializable

@Resource("/api")
class Routing {
    @Resource("/login")
    class Login(val parent: Routing = Routing())

    @Resource("/companies")
    class Companies(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: Companies = Companies(), val id: Int)

        @Resource("update")
        data class Update(val parent: Companies = Companies(), val id: Int, val name: String)

        @Resource("name/{name}")
        data class Name(val parent: Companies = Companies(), val name: String)

        @Resource("new")
        data class New(val parent: Companies = Companies(), val name: String)
    }

    @Resource("/projects")
    class Projects(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: Projects = Projects(), val id: Int)

        @Resource("update")
        data class Update(val parent: Projects = Projects(), val id: Int, val name: String, val description: String? = null)

        @Resource("name/{name}")
        data class Name(val parent: Projects = Projects(), val name: String)

        @Resource("new")
        data class New(val parent: Projects = Projects(), val name: String, val description: String? = null)

        @Resource("dependencies")
        data class Dependencies(val parent: Projects = Projects()) {
            @Resource("{id}")
            data class Id(val parent: Dependencies = Dependencies(), val id: Int)
        }
    }

    @Resource("/project-steps")
    data class ProjectSteps(val parent: Routing = Routing()) {
        @Resource("update")
        data class Update(val parent: ProjectSteps = ProjectSteps()) {
            @Serializable
            data class Body(
                val projectId: Int,
                val steps: List<Step>
            )
        }

        @Resource("{id}")
        data class Id(val parent: ProjectSteps = ProjectSteps(), val id: Int)
    }

    @Resource("/project-variables")
    class ProjectVariables(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: ProjectVariables = ProjectVariables(), val id: Int)

        @Resource("new")
        data class New(val parent: ProjectVariables = ProjectVariables()) {
            @Serializable
            data class Body(
                val projectId: Int,
                val name: String,
                val values: List<ProjectVariableValue>
            ) {
                init {
                    // -> enforce default value present
                    require(values.any { it.stageId == null && it.companyId == null }) { "No default Variable Value" }
                }
            }
        }

        @Resource("update")
        data class Update(val parent: ProjectVariables = ProjectVariables())
    }

    @Resource("/project-releases")
    class ProjectReleases(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: ProjectReleases = ProjectReleases(), val id: Int)

        @Resource("getByName")
        data class GetByName(val parent: Stages = Stages(), val projectName: String, val version: String)

        @Resource("new")
        data class New(val parent: ProjectReleases = ProjectReleases()) {
            @Serializable
            data class Body(
                val projectId: Int,
                val version: String,
                val deployables: Map<String, String>
            )
        }

        @Resource("newByName")
        data class NewByName(val parent: ProjectReleases = ProjectReleases()) {
            @Serializable
            data class Body(
                val project: String,
                val version: String,
                val deployables: Map<String, String>
            )
        }

        @Resource("sort")
        data class Sort(
            val parent: ProjectReleases = ProjectReleases(),
            val projectId: Int,
            val sort: String = "date",
            val order: String = "desc",
            val page: Int = 1,
            val size: Int = 10
        )
    }

    @Resource("/rollout-executions")
    class RolloutExecutions(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: RolloutExecutions = RolloutExecutions(), val id: Int)

        @Resource("newByName")
        data class NewByCompanyName(
            val parent: RolloutExecutions = RolloutExecutions(),
        ) {
            @Serializable
            data class Body(
                val releaseId: Int,
                val stage: String,
                val companies: List<String>
            )
        }

        @Resource("sortByReleaseId")
        data class SortByReleaseId(
            val parent: RolloutExecutions = RolloutExecutions(),
            val releaseId: Int,
            val order: String = "desc",
            val page: Int = 1,
            val size: Int = 5
        )

        @Resource("sort")
        data class Sort(
            val parent: RolloutExecutions = RolloutExecutions(),
            val projectId: Int,
            val sort: String = "date",
            val order: String = "desc",
            val page: Int = 1,
            val size: Int = 10
        )
    }

    @Resource("/stages")
    class Stages(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: Stages = Stages(), val id: Int)

        @Resource("update")
        data class Update(val parent: Stages = Stages(), val id: Int, val name: String, val ordering: Int)

        @Resource("name/{name}")
        data class Name(val parent: Stages = Stages(), val name: String)

        @Resource("new")
        data class New(val parent: Stages = Stages(), val name: String, val ordering: Int)
    }

    @Resource("/company-projects")
    class CompanyProjects(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: CompanyProjects = CompanyProjects(), val id: Int)

        @Resource("by-project/{projectId}")
        data class ByProject(val parent: CompanyProjects = CompanyProjects(), val projectId: Int)

        @Resource("new")
        data class New(val parent: CompanyProjects = CompanyProjects(), val projectId: Int, val companyId: Int)
    }

    @Resource("/company-project-stages")
    class CompanyProjectStages(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: CompanyProjectStages = CompanyProjectStages(), val id: Int)

        @Resource("new")
        data class New(
            val parent: CompanyProjectStages = CompanyProjectStages(),
            val stageId: Int,
            val companyProjectId: Int
        )
    }

    @Resource("/deployables")
    class Deployables(val parent: Routing = Routing()) {
        @Resource("{id}")
        data class Id(val parent: Deployables = Deployables(), val id: Int)

        @Resource("name")
        data class Name(val parent: Deployables = Deployables())

        @Resource("sort")
        data class Sort(
            val parent: Deployables = Deployables(),
            val name: String,
            val sort: String = "date",
            val order: String = "desc",
            val page: Int = 1,
            val size: Int = 10
        )

        @Resource("new")
        data class New(val parent: Deployables = Deployables()) {
            @Serializable
            data class Body(
                val request: Request,
                val file: ByteArray? = null
            )

            @Serializable
            data class Request(
                val name: String,
                val version: String,
                val data: DeployableData
            )

        }
    }

    @Resource("/api-keys")
    class ApiKeys(val parent: Routing = Routing()) {
        @Resource("{name}")
        data class ByName(
            val parent: ApiKeys = ApiKeys(),
            val name: String
        )
    }

    @Resource("/aws-accounts")
    class AwsAccounts(val parent: Routing = Routing()) {
        @Resource("/overview")
        data class Overview(val parent: AwsAccounts = AwsAccounts())
    }
}
